import React, { useState, useEffect, useRef } from 'react';
import { Save, Eye, EyeOff, Users, Clock, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PopupTab = ({ getStoredPassword }) => {
  const [settings, setSettings] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const passwordRef = useRef(null);

  useEffect(() => {
    passwordRef.current = getStoredPassword();
    const fetch_ = async () => {
      const pw = passwordRef.current;
      try {
        const [sRes, subRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/popup-settings?password=${pw}`),
          fetch(`${API_URL}/api/admin/popup-subscribers?password=${pw}`)
        ]);
        if (sRes.ok) setSettings(await sRes.json());
        if (subRes.ok) setSubscribers(await subRes.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/admin/popup-settings?password=${pw}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success('Popup settings saved');
      else toast.error('Failed to save');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;
  if (!settings) return <div className="text-center text-white/50 py-8">Failed to load settings</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Popup Banner</h3>
          <p className="text-white/40 text-sm">Manage the lead capture popup that appears on your site</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={saveSettings} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" disabled={saving} data-testid="save-popup-settings">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.enabled ? <Eye className="w-5 h-5 text-green-400" /> : <EyeOff className="w-5 h-5 text-white/30" />}
            <div>
              <h4 className="text-white font-medium text-sm">{settings.enabled ? 'Popup is Live' : 'Popup is Disabled'}</h4>
              <p className="text-white/40 text-xs">{settings.enabled ? 'Visitors will see the popup after the configured delay' : 'Enable to start showing the popup to visitors'}</p>
            </div>
          </div>
          <button onClick={() => setSettings({...settings, enabled: !settings.enabled})}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${settings.enabled ? 'bg-green-500/70' : 'bg-white/15'}`}
            data-testid="popup-enabled-toggle"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm ${settings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* Content Settings */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
        <h4 className="text-white font-medium text-sm">Content</h4>
        <div>
          <label className="text-white/50 text-xs block mb-1.5">Headline</label>
          <Input value={settings.headline} onChange={e => setSettings({...settings, headline: e.target.value})} className="bg-white/5 border-white/10 text-white" data-testid="popup-headline-input" />
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1.5">Supporting Text</label>
          <textarea value={settings.subtext} onChange={e => setSettings({...settings, subtext: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50 min-h-[60px] resize-y" data-testid="popup-subtext-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">CTA Button Text</label>
            <Input value={settings.cta_text} onChange={e => setSettings({...settings, cta_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" />
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Badge Text</label>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
              <Input value={settings.show_badge_text} onChange={e => setSettings({...settings, show_badge_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., Exclusive" />
            </div>
          </div>
        </div>
      </div>

      {/* Success State */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
        <h4 className="text-white font-medium text-sm">After Submission</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Success Title</label>
            <Input value={settings.success_title} onChange={e => setSettings({...settings, success_title: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" />
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Success Text</label>
            <Input value={settings.success_text} onChange={e => setSettings({...settings, success_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" />
          </div>
        </div>
      </div>

      {/* Behaviour */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
        <h4 className="text-white font-medium text-sm">Behaviour</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Show Delay (seconds)</label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/30 flex-shrink-0" />
              <Input type="number" value={settings.delay_seconds} onChange={e => setSettings({...settings, delay_seconds: parseInt(e.target.value) || 5})} className="bg-white/5 border-white/10 text-white text-xs" min={1} max={60} />
            </div>
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Dismiss Duration (days)</label>
            <Input type="number" value={settings.dismiss_days} onChange={e => setSettings({...settings, dismiss_days: parseInt(e.target.value) || 7})} className="bg-white/5 border-white/10 text-white text-xs" min={1} max={90} />
          </div>
        </div>
      </div>

      {/* Subscribers */}
      <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            <h4 className="text-white font-medium text-sm">Subscribers</h4>
          </div>
          <span className="text-white/30 text-xs">{subscribers.length} total</span>
        </div>
        {subscribers.length === 0 ? (
          <div className="px-5 py-8 text-center text-white/20 text-sm">No subscribers yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Name</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Email</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Phone</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map(sub => (
                  <tr key={sub.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5 text-white">{sub.first_name} {sub.last_name}</td>
                    <td className="px-4 py-2.5 text-white/60">{sub.email}</td>
                    <td className="px-4 py-2.5 text-white/60">{sub.phone || '—'}</td>
                    <td className="px-4 py-2.5 text-white/30">{new Date(sub.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
